import * as React from 'react';

import clsxm from '@/lib/clsxm';

export enum TypographyColor {
  DEFAULT = 'DEFAULT',
  LIGHT = 'LIGHT',
  PRIMARY = 'PRIMARY',
  PRIMARY_EXTRA_DARK = 'PRIMARY_DARK',
  PRIMARY_CONTRAST = 'PRIMARY_CONTRAST',
  INDIGO = 'INDIGO',
}

type Family = 'romek-rounded' | 'dm-sans';
type Weight = 'bold' | 'normal' | 'medium' | 'light';
type Decoraton = 'underline';
type Style = 'italic';

const colorClassName: Record<TypographyColor, string> = {
  [TypographyColor.DEFAULT]: 'text-typography',
  [TypographyColor.LIGHT]: 'text-gray-800',
  [TypographyColor.PRIMARY]: 'text-primary',
  [TypographyColor.PRIMARY_EXTRA_DARK]: 'text-primary-extra-dark',
  [TypographyColor.PRIMARY_CONTRAST]: 'text-primary-contrast',
  [TypographyColor.INDIGO]: 'text-indigo',
};

const familyClassName: Record<Family, string> = {
  'romek-rounded': 'font-romek-rounded',
  'dm-sans': 'font-dm-sans',
};

const weightClassName: Record<Weight, string> = {
  light: 'font-light',
  normal: 'font-normal',
  medium: 'font-medium',
  bold: 'font-bold',
};

const decorationClassName: Record<Decoraton, string> = {
  underline: 'underline',
};

const styleClassName: Record<Style, string> = {
  italic: 'italic',
};

export type TypographyProps = {
  children: string | React.ReactElement | React.ReactNode;
  className?: string;
  as?: React.ElementType;
  color?: TypographyColor;
  inline?: boolean;
  onClick?: (e: React.MouseEvent) => void;
  overline?: boolean;
  overlineClassName?: string;
  family?: Family;
  weight?: Weight;
  decoration?: Decoraton;
  style?: Style;
};

export const Typography = () => <></>;

type BaseTypographyProps = TypographyProps & {
  baseClassName: string;
};

const BaseTypography = ({
  baseClassName,
  className,
  as,
  color = TypographyColor.DEFAULT,
  family = 'dm-sans',
  weight = 'normal',
  inline,
  children,
  overline,
  overlineClassName,
  decoration,
  style,
  ...rest
}: BaseTypographyProps) => {
  const Tag = as || 'div';

  const propClassNames = clsxm(
    colorClassName[color],
    familyClassName[family],
    weightClassName[weight],
    decoration && decorationClassName[decoration],
    style && styleClassName[style]
  );

  let updatedChildren;

  if (typeof children === 'string') {
    updatedChildren = children.split('\\n').map((child, c) => {
      const updatedChild: React.ReactNode[] = [];

      const parts = child.split('*');
      const numParts = parts.length;

      // if there is 1 or less *, then we dont need to do anything
      if (numParts > 2) {
        let isAsterisk = false;
        parts.forEach((substring, i) => {
          const key = `${c}_${i}`;
          if (isAsterisk && i !== numParts - 1) {
            updatedChild.push(
              <span key={key} className='italic text-primary'>
                {substring}
              </span>
            );
          } else {
            updatedChild.push(
              <React.Fragment key={key}>{substring}</React.Fragment>
            );
          }

          isAsterisk = !isAsterisk;
        });
      } else {
        updatedChild.push(child);
      }

      return (
        <React.Fragment key={c}>
          {updatedChild}
          <br />
        </React.Fragment>
      );
    });
  }

  if (!updatedChildren) {
    updatedChildren = children;
  }

  const updatedClassName = clsxm(
    'whitespace-pre-line break-words antialiased',
    inline && 'inline',
    baseClassName,
    propClassNames,
    className
  );

  return (
    <>
      {overline && (
        <div
          className={clsxm(
            'mb-2 h-1 w-12 rounded-lg bg-primary md:w-20',
            overlineClassName
          )}
        />
      )}
      <Tag
        className={updatedClassName}
        style={{ wordBreak: 'break-word' }}
        {...rest}
      >
        {updatedChildren}
      </Tag>
    </>
  );
};

/** @deprecated */
Typography.Header = (props: TypographyProps) => (
  <BaseTypography
    as='h1'
    baseClassName='text-4xl font-medium sm:text-5xl sm:leading-[3.5rem]'
    weight='bold'
    {...props}
  />
);

/** @deprecated */
Typography.HeaderSmall = (props: TypographyProps) => (
  <BaseTypography
    as='h2'
    baseClassName='text-3xl font-extrabold sm:text-4xl sm:leading-[3rem]'
    weight='bold'
    {...props}
  />
);

/** @deprecated */
Typography.HeaderXSmall = (props: TypographyProps) => (
  <BaseTypography
    as='h2'
    baseClassName='text-2xl font-extrabold sm:text-3xl sm:leading-[3rem]'
    weight='bold'
    {...props}
  />
);

/** @deprecated */
Typography.Caption = (props: TypographyProps) => (
  <BaseTypography
    baseClassName='text-base font-semibold uppercase text-typography-light'
    {...props}
  />
);

/** @deprecated */
Typography.Text = (props: TypographyProps) => (
  <BaseTypography baseClassName='text-lg' {...props} />
);

/** @deprecated */
Typography.TextXXLarge = (props: TypographyProps) => (
  <BaseTypography baseClassName='text-3xl' {...props} />
);

/** @deprecated */
Typography.TextXLarge = (props: TypographyProps) => (
  <BaseTypography baseClassName='text-2xl' {...props} />
);

/** @deprecated */
Typography.TextLarge = (props: TypographyProps) => (
  <BaseTypography baseClassName='text-xl' {...props} />
);

/** @deprecated */
Typography.TextSmall = (props: TypographyProps) => (
  <BaseTypography baseClassName='text-base' {...props} />
);

/** @deprecated */
Typography.TextXSmall = (props: TypographyProps) => (
  <BaseTypography baseClassName='text-xs' {...props} />
);

/**
 * Display 1: Romek Light, 120px
 */
Typography.Display1 = (props: TypographyProps) => (
  <BaseTypography
    baseClassName='text-[120px] -tracking-[1.2px] antialiased'
    family='romek-rounded'
    weight='light'
    {...props}
  />
);

/**
 * Display 2: Romek Light, 80px
 */
Typography.Display2 = (props: TypographyProps) => (
  <BaseTypography
    baseClassName='text-[80px] -tracking-[.8px] antialiased'
    family='romek-rounded'
    weight='light'
    {...props}
  />
);

/**
 * Display 3: Romek Light, 64px
 */
Typography.Display3 = (props: TypographyProps) => (
  <BaseTypography
    baseClassName='text-[64px] -tracking-[.64px] antialiased'
    family='romek-rounded'
    weight='light'
    {...props}
  />
);

/**
 * Heading 1: Romek Light, 48px
 */
Typography.Heading1 = (props: TypographyProps) => (
  <BaseTypography
    as='h1'
    baseClassName='text-[32px] md:text-[40px] lg:text-[48px] -tracking-[1.44px] leading-[120%] antialiased whitespace-pre-wrap'
    family='romek-rounded'
    weight='light'
    {...props}
  />
);

/**
 * Heading 2: Romek Light, 40px
 */
Typography.Heading2 = (props: TypographyProps) => (
  <BaseTypography
    as='h2'
    baseClassName='text-[24px] md:text-[32px] lg:text-[40px] -tracking-[1.44px] antialiased'
    family='romek-rounded'
    weight='light'
    {...props}
  />
);

/**
 * Heading 3: Romek Light, 32px
 */
Typography.Heading3 = (props: TypographyProps) => (
  <BaseTypography
    as='h3'
    baseClassName='text-[32px] tracking-[.08px] antialiased'
    family='romek-rounded'
    weight='light'
    {...props}
  />
);

/**
 * Heading 3 (highlight): Romek Regular Italic, 32px
 */
Typography.Heading3Highlight = (props: TypographyProps) => (
  <Typography.Heading3
    {...props}
    className={clsxm('-tracking-[.64px]', props.className)}
    color={TypographyColor.PRIMARY}
    style='italic'
  />
);

/**
 * Heading 4: DM Sans Bold, 24px
 */
Typography.Heading4 = (props: TypographyProps) => (
  <BaseTypography
    as='h4'
    baseClassName='text-[24px] tracking-[.06px]'
    family='dm-sans'
    weight='bold'
    {...props}
  />
);

/**
 * Heading 5: Romek Light, 24px
 */
Typography.Heading5 = (props: TypographyProps) => (
  <BaseTypography
    as='h5'
    baseClassName='text-[24px] -tracking-[.72px] leading-[130%] antialiased'
    family='romek-rounded'
    weight='light'
    {...props}
  />
);

/**
 * Heading 6: DM Sans Medium, 20px
 */
Typography.Heading6 = (props: TypographyProps) => (
  <BaseTypography
    as='h6'
    baseClassName='text-[16px] lg:text-[20px] tracking-[.05px]'
    family='dm-sans'
    weight='medium'
    {...props}
  />
);

Typography.Eyebrow = (props: TypographyProps) => (
  <BaseTypography
    baseClassName='text-[20px] tracking-[.05px] leading-[130%] antialiased'
    family='dm-sans'
    weight='medium'
    {...props}
  />
);

/**
 * Subtitle 1: Romek Light, 18px
 */
Typography.Subtitle1 = (props: TypographyProps) => (
  <BaseTypography
    baseClassName='text-[18px] tracking-[.045px]'
    family='romek-rounded'
    weight='light'
    {...props}
  />
);

/**
 * Subtitle 2: DM Sans Bold, 16px
 */
Typography.Subtitle2 = (props: TypographyProps) => (
  <BaseTypography
    baseClassName='text-[16px] tracking-[.04px] antialiased'
    family='dm-sans'
    weight='normal'
    {...props}
  />
);

/**
 * Subtitle 3: DM Sans Bold, 12px
 */
Typography.Subtitle3 = (props: TypographyProps) => (
  <BaseTypography
    baseClassName='text-[12px] tracking-[.03px]'
    family='dm-sans'
    weight='bold'
    {...props}
  />
);

/**
 * Subtitle 4: DM Sans MEDIUM, 12px
 */
Typography.Subtitle4 = (props: TypographyProps) => (
  <BaseTypography
    baseClassName='text-[12px] tracking-[.06px] uppercase'
    family='dm-sans'
    weight='medium'
    {...props}
  />
);

/**
 * Subtitle 5: DM Sans Medium, 12px
 */
Typography.Subtitle5 = (props: TypographyProps) => (
  <BaseTypography
    baseClassName='text-[12px] tracking-[.15px]'
    family='dm-sans'
    weight='medium'
    {...props}
  />
);

/**
 * Subtitle 6: DM Sans Bold, 10px
 */
Typography.Subtitle6 = (props: TypographyProps) => (
  <BaseTypography
    baseClassName='text-[10px] tracking-[.03px] leading-none uppercase'
    family='dm-sans'
    weight='bold'
    {...props}
  />
);

/**
 * Body 1: DM Sans Regular, 20px
 */
Typography.Body1 = (props: TypographyProps) => (
  <BaseTypography
    baseClassName='text-[20px] tracking-[.1px]'
    family='dm-sans'
    {...props}
  />
);

/**
 * Body 2: DM Sans Regular, 16px
 */
Typography.Body2 = (props: TypographyProps) => (
  <BaseTypography
    baseClassName='text-[18px] tracking-[.08px] leading-[144%]'
    family='dm-sans'
    {...props}
  />
);

/**
 * Body 3: DM Sans Medium, 14px
 */
Typography.Body3 = (props: TypographyProps) => (
  <BaseTypography
    baseClassName='text-[14px] tracking-[.035px]'
    family='dm-sans'
    weight='medium'
    {...props}
  />
);
/**
 * Body 3.5: DM Sans Regular, 14px
 */
Typography.Body35 = (props: TypographyProps) => (
  <BaseTypography
    baseClassName='text-[14px] tracking-[.035px] leading-[144%]'
    family='dm-sans'
    weight='medium'
    {...props}
  />
);

/**
 * Body 4: DM Sans Medium, 12px
 */
Typography.Body4 = (props: TypographyProps) => (
  <BaseTypography
    baseClassName='text-[12px] tracking-[.03px]'
    family='dm-sans'
    weight='medium'
    {...props}
  />
);

/**
 * Body 5: DM Sans Regular, 12px
 */
Typography.Body5 = (props: TypographyProps) => (
  <BaseTypography
    baseClassName='text-[12px] tracking-[.03px]'
    family='dm-sans'
    {...props}
  />
);

/**
 * Menu 1: DM Sans Bold, 19px
 */
Typography.Menu1 = (props: TypographyProps) => (
  <BaseTypography
    baseClassName='text-[19px] tracking-[.057px]'
    family='dm-sans'
    weight='bold'
    {...props}
  />
);

/**
 * Menu 2: DM Sans Medium, 14px
 */
Typography.Menu2 = (props: TypographyProps) => (
  <BaseTypography
    baseClassName='text-[14px] tracking-[.07px]'
    family='dm-sans'
    weight='medium'
    {...props}
  />
);

/**
 * Button 1: DM Sans Bold, 20ox
 */
Typography.Button1 = (props: TypographyProps) => (
  <BaseTypography
    baseClassName='text-[20px] tracking-[.035px]'
    family='dm-sans'
    weight='bold'
    {...props}
  />
);

/**
 * Button 2: DM Sans Bold, 18px
 */
Typography.Button2 = (props: TypographyProps) => (
  <BaseTypography
    baseClassName='text-[18px] tracking-[.054px]'
    family='dm-sans'
    weight='bold'
    {...props}
  />
);

/**
 * Button 3: DM Sans Bold, 16px
 */
Typography.Button3 = (props: TypographyProps) => (
  <BaseTypography
    baseClassName='text-[16px]'
    family='dm-sans'
    weight='bold'
    {...props}
  />
);

/**
 * Button 4: DM Sans Bold, 14px
 */
Typography.Button4 = (props: TypographyProps) => (
  <BaseTypography
    baseClassName='text-[14px]'
    family='dm-sans'
    weight='bold'
    {...props}
  />
);

/**
 * inline utilities
 */
Typography.InlineItalic = (props: TypographyProps) => (
  <span className='inline italic'>{props.children}</span>
);

Typography.InlineBold = (props: TypographyProps) => (
  <span className='bold inline'>{props.children}</span>
);

Typography.InlineUnderline = (props: TypographyProps) => (
  <span className='inline underline'>{props.children}</span>
);

export default Typography;
